// src/pages/admin/admin-talleres.tsx
// REFACTORIZADO: Usa el hook useTaller para centralizar la lógica

import React, { useEffect, useState } from 'react';
import { deleteDoc, doc, getDoc, addDoc, collection, Timestamp } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import UserNav from '../../components/navbar/user-nav';

// Importar el hook y tipos desde useTaller
import { useTaller, Taller, TallerFilters } from '../../hooks/useTaller';

// Importar componentes de loading
import { LoadingMessage, ErrorState, EmptyState } from '../../components/loading-states';

export default function AdminTalleres() {
  const navigate = useNavigate();

  // ========== USAR EL HOOK useTaller ==========
  // Esto reemplaza ~90 líneas de lógica duplicada (interfaces, calcularEstado, fetch, filtrado)
  const {
    talleres,
    loading,
    error,
    fetchAllTalleres,
    filtrarTalleres,
    formatearFecha
  } = useTaller();

  // Estados locales (solo UI)
  const [menuAbierto, setMenuAbierto] = useState<string | null>(null);
  const [filtros, setFiltros] = useState<TallerFilters>({
    nombre: '',
    mes: '',
    costo: 'todos',
    creditos: 'todos',
    estado: 'todos'
  });

  // Cargar talleres al montar el componente
  useEffect(() => {
    fetchAllTalleres(true); // true = incluir conteo de inscritos
  }, [fetchAllTalleres]);

  // Aplicar filtros usando la función del hook
  const talleresFiltrados = filtrarTalleres(talleres, filtros);

  // Cerrar el menú al hacer clic fuera de él
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.position-relative')) {
        setMenuAbierto(null);
      }
    };

    if (menuAbierto) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [menuAbierto]);

  // Handlers de acciones
  const handleDelete = async (id: string) => {
    const result = await Swal.fire({
      title: '¿Eliminar taller?',
      text: 'Esta acción no se puede deshacer',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        await deleteDoc(doc(db, 'taller', id));
        // Recargar talleres después de eliminar
        fetchAllTalleres(true);
        Swal.fire('Eliminado', 'El taller ha sido eliminado.', 'success');
      } catch (err) {
        Swal.fire('Error', 'No se pudo eliminar el taller.', 'error');
      }
    }
  };

  const handleDuplicate = async (id: string) => {
    const result = await Swal.fire({
      title: 'Duplicar Taller',
      html: `
        <p>Se creará una copia del taller con:</p>
        <ul class="text-start">
          <li>Mismo contenido y configuración</li>
          <li>Sin inscritos ni asistencias</li>
          <li>Nueva fecha (deberás actualizarla)</li>
        </ul>
        <p class="text-muted small">El nuevo taller se abrirá para edición.</p>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, duplicar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        const tallerRef = doc(db, 'taller', id);
        const tallerSnap = await getDoc(tallerRef);

        if (!tallerSnap.exists()) {
          Swal.fire('Error', 'No se encontró el taller original.', 'error');
          return;
        }

        const tallerOriginal = tallerSnap.data();

        const generarNuevoId = (nombre: string): string => {
          const letras = nombre.replace(/[^a-zA-Z]/g, '').toLowerCase().slice(0, 3) || 'evt';
          const now = new Date();
          const dia = String(now.getDate()).padStart(2, '0');
          const mes = String(now.getMonth() + 1).padStart(2, '0');
          const anio = String(now.getFullYear()).slice(-2);
          const random = Math.floor(Math.random() * 100).toString().padStart(2, '0');
          return `${letras}${dia}${mes}${anio}${random}`;
        };

        const nuevoTaller = {
          ...tallerOriginal,
          taller_id: generarNuevoId(tallerOriginal.nombre || 'taller'),
          nombre: `${tallerOriginal.nombre} (Copia)`,
          fecha: Timestamp.fromDate(new Date()),
          fechaCreacion: Timestamp.fromDate(new Date()),
          activo: false,
          estado: 'proximo'
        };

        const nuevoDocRef = await addDoc(collection(db, 'taller'), nuevoTaller);

        await Swal.fire({
          icon: 'success',
          title: 'Taller Duplicado',
          html: `
            <p>Se ha creado una copia del taller.</p>
            <p class="text-warning"><strong>Importante:</strong> Actualiza la fecha y activa el taller.</p>
          `,
          confirmButtonText: 'Ir a Editar',
          confirmButtonColor: '#28a745'
        });

        navigate(`/edit-taller/${nuevoDocRef.id}`);
      } catch (err) {
        console.error('Error al duplicar taller:', err);
        Swal.fire('Error', 'No se pudo duplicar el taller.', 'error');
      }
    }
  };

  const limpiarFiltros = () => {
    setFiltros({
      nombre: '',
      mes: '',
      costo: 'todos',
      creditos: 'todos',
      estado: 'todos'
    });
  };

  // Actualizar un filtro específico
  const actualizarFiltro = (campo: keyof TallerFilters, valor: string) => {
    setFiltros(prev => ({ ...prev, [campo]: valor }));
  };

  // ========== RENDER ==========

  // Estado de carga - usando componente LoadingMessage
  if (loading) {
    return (
      <>
        <UserNav />
        <div className="container mt-5">
          <LoadingMessage message="Cargando talleres..." subMessage="Por favor espera" />
        </div>
        <FooterTop bg="theme-bg" />
        <Footer />
      </>
    );
  }

  // Estado de error - usando componente ErrorState
  if (error) {
    return (
      <>
        <UserNav />
        <div className="container mt-5">
          <ErrorState
            title="Error al cargar talleres"
            message={error}
            onRetry={() => fetchAllTalleres(true)}
          />
        </div>
        <FooterTop bg="theme-bg" />
        <Footer />
      </>
    );
  }

  return (
    <>
      <UserNav />
      <div className="container mt-5">
        <h1 className="text-center mb-4">Administración de Talleres</h1>

        <div className="d-flex justify-content-between align-items-center mb-3">
          <button className="btn btn-success" onClick={() => navigate('/admin-create-taller')}>
            + Agregar Taller
          </button>
        </div>

        {/* Filtros */}
        <div className="card mb-4 p-3 shadow-sm">
          <h5 className="mb-3">
            <i className="fa-solid fa-filter me-2"></i>Filtros de Búsqueda
          </h5>
          <div className="row g-3">
            <div className="col-md-6 col-lg-3">
              <label className="form-label small fw-bold">Nombre del Taller</label>
              <input
                type="text"
                className="form-control"
                placeholder="Buscar por nombre..."
                value={filtros.nombre || ''}
                onChange={(e) => actualizarFiltro('nombre', e.target.value)}
              />
            </div>

            <div className="col-md-6 col-lg-3">
              <label className="form-label small fw-bold">Mes</label>
              <input
                type="month"
                className="form-control"
                value={filtros.mes || ''}
                onChange={(e) => actualizarFiltro('mes', e.target.value)}
              />
            </div>

            <div className="col-md-6 col-lg-3">
              <label className="form-label small fw-bold">Tipo de Costo</label>
              <select
                className="form-select"
                value={filtros.costo || 'todos'}
                onChange={(e) => actualizarFiltro('costo', e.target.value)}
              >
                <option value="todos">Todos</option>
                <option value="gratuito">Gratuito</option>
                <option value="pago">Pago</option>
              </select>
            </div>

            <div className="col-md-6 col-lg-2">
              <label className="form-label small fw-bold">Créditos</label>
              <select
                className="form-select"
                value={filtros.creditos || 'todos'}
                onChange={(e) => actualizarFiltro('creditos', e.target.value)}
              >
                <option value="todos">Todos</option>
                <option value="con_creditos">Con Créditos</option>
                <option value="sin_creditos">Sin Créditos</option>
              </select>
            </div>

            <div className="col-md-6 col-lg-2">
              <label className="form-label small fw-bold">Estado</label>
              <select
                className="form-select"
                value={filtros.estado || 'todos'}
                onChange={(e) => actualizarFiltro('estado', e.target.value)}
              >
                <option value="todos">Todos</option>
                <option value="proximo">Próximo</option>
                <option value="en_curso">En Curso</option>
                <option value="finalizado">Finalizado</option>
                <option value="cancelado">Cancelado</option>
              </select>
            </div>
          </div>

          <div className="mt-3 text-end">
            <button className="btn btn-outline-secondary btn-sm" onClick={limpiarFiltros}>
              <i className="fa-solid fa-times me-1"></i>Limpiar Filtros
            </button>
            <span className="ms-3 text-muted small">
              Mostrando {talleresFiltrados.length} de {talleres.length} talleres
            </span>
          </div>
        </div>

        {/* Estado vacío - usando componente EmptyState */}
        {talleres.length === 0 ? (
          <EmptyState
            icon="fa-solid fa-chalkboard-user"
            title="No hay talleres registrados"
            message="Comienza creando tu primer taller"
            actionText="Crear Taller"
            onAction={() => navigate('/admin-create-taller')}
          />
        ) : talleresFiltrados.length === 0 ? (
          <div className="alert alert-info">
            <i className="fa-solid fa-info-circle me-2"></i>
            No se encontraron talleres con los filtros aplicados.
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-bordered">
              <thead className="table-light">
                <tr>
                  <th>Nombre</th>
                  <th>Fecha</th>
                  <th>Horario</th>
                  <th>Lugar</th>
                  <th>Organizador</th>
                  <th>Cupo</th>
                  <th>Inscritos</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {talleresFiltrados.map((taller: Taller) => (
                  <tr key={taller.id}>
                    <td>{taller.nombre}</td>
                    <td>
                      {taller.fecha?.toDate?.().toLocaleDateString('es-MX') ||
                       taller.fechaInicio?.toDate?.().toLocaleDateString('es-MX') || 'N/A'}
                    </td>
                    <td>
                      {taller.horaInicio} - {taller.horaTermino}
                    </td>
                    <td>{taller.lugar}</td>
                    <td>{taller.organizadorPrincipal || taller.expositor}</td>
                    <td>{taller.cupoMaximo || taller.cupo}</td>
                    <td>
                      <span className="badge bg-info">
                        {taller.inscritos || 0}
                      </span>
                    </td>
                    <td>
                      {taller.estadoCalculado === 'proximo' && (
                        <span className="badge bg-primary">Próximo</span>
                      )}
                      {taller.estadoCalculado === 'en_curso' && (
                        <span className="badge bg-success">En Curso</span>
                      )}
                      {taller.estadoCalculado === 'finalizado' && (
                        <span className="badge bg-secondary">Finalizado</span>
                      )}
                      {taller.estadoCalculado === 'cancelado' && (
                        <span className="badge bg-danger">Cancelado</span>
                      )}
                      {taller.estadoCalculado === 'sin_fecha' && (
                        <span className="badge bg-warning text-dark">Sin Fecha</span>
                      )}
                    </td>
                    <td className="position-relative">
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => setMenuAbierto(menuAbierto === taller.id ? null : taller.id || null)}
                        aria-label="Abrir menú de acciones"
                      >
                        <i className="fa-solid fa-gear" aria-hidden="true"></i>
                      </button>

                      {menuAbierto === taller.id && (
                        <div
                          className="position-absolute end-0 mt-1 bg-white border rounded shadow-sm"
                          style={{ zIndex: 1000, minWidth: '180px' }}
                        >
                          <div className="list-group list-group-flush">
                            <button
                              className="list-group-item list-group-item-action d-flex align-items-center"
                              onClick={() => {
                                navigate(`/ver-taller-ind/${taller.id}`);
                                setMenuAbierto(null);
                              }}
                            >
                              <i className="fa-solid fa-eye me-2" aria-hidden="true"></i>
                              Ver Detalles
                            </button>
                            <button
                              className="list-group-item list-group-item-action d-flex align-items-center"
                              onClick={() => {
                                navigate(`/edit-taller/${taller.id}`);
                                setMenuAbierto(null);
                              }}
                            >
                              <i className="fa-solid fa-pen-to-square me-2" aria-hidden="true"></i>
                              Editar
                            </button>
                            <button
                              className="list-group-item list-group-item-action d-flex align-items-center text-success"
                              onClick={() => {
                                handleDuplicate(taller.id as string);
                                setMenuAbierto(null);
                              }}
                            >
                              <i className="fa-solid fa-copy me-2" aria-hidden="true"></i>
                              Duplicar
                            </button>
                            <button
                              className="list-group-item list-group-item-action d-flex align-items-center text-danger"
                              onClick={() => {
                                handleDelete(taller.id as string);
                                setMenuAbierto(null);
                              }}
                            >
                              <i className="fa-solid fa-trash me-2" aria-hidden="true"></i>
                              Eliminar
                            </button>
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
